import { TextField, Typography, Box } from '@mui/material';
import { ReactNode, useState } from 'react';
import { BulkModal } from './BulkModal';

interface BulkDeleteModalProperties<T extends { id: string }> {
  open: boolean;
  onClose: () => void;
  selectedItems: T[];
  onDeleteComplete?: () => void;
  deleteItem: (item: T) => Promise<void>;
  itemLabel: { singular: string; plural: string };
  dataTestId?: string;
  children?: ReactNode;
  description?: (count: number) => string;
}

const CONFIRMATION_WORD = 'DELETE';

export const BulkDeleteModal = <T extends { id: string }>({
  open,
  onClose,
  selectedItems,
  onDeleteComplete,
  deleteItem,
  itemLabel,
  dataTestId,
  children,
  description,
}: BulkDeleteModalProperties<T>) => {
  const [confirmationText, setConfirmationText] = useState('');
  const label = selectedItems.length === 1 ? itemLabel.singular : itemLabel.plural;
  const isConfirmDisabled = confirmationText.toUpperCase() !== CONFIRMATION_WORD;

  const handleClose = () => {
    setConfirmationText('');
    onClose();
  };

  return (
    <BulkModal
      open={open}
      onClose={handleClose}
      selectedItems={selectedItems}
      onComplete={onDeleteComplete}
      performOperation={deleteItem}
      labels={{
        title: `Delete ${label}`,
        action: 'delete',
        actionPastTense: 'deleted',
        actionPresent: 'Deleting',
        confirmButton: 'Delete all',
        itemSingular: itemLabel.singular,
        itemPlural: itemLabel.plural,
        description:
          description ??
          (count =>
            `${count} ${count === 1 ? itemLabel.singular : itemLabel.plural} will be permanently deleted. This action cannot be undone.`),
      }}
      buttonColor='error'
      dataTestId={dataTestId}
      isConfirmDisabled={isConfirmDisabled}
    >
      {children}
      <Box sx={{ mt: 2 }}>
        <Typography variant='body2' color='textSecondary' sx={{ mb: 1 }}>
          Type <strong>{CONFIRMATION_WORD}</strong> to confirm
        </Typography>
        <TextField
          value={confirmationText}
          onChange={event => setConfirmationText(event.target.value)}
          placeholder={CONFIRMATION_WORD}
          fullWidth
          size='small'
          autoComplete='off'
          slotProps={{
            htmlInput: {
              'data-testid': 'bulk-delete-confirmation-input',
            },
          }}
        />
      </Box>
    </BulkModal>
  );
};
